"use client";

import { useMemo, useState } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { compact, usd } from "@/lib/format";
import type { MonthlySeries } from "@/lib/types";
import { TOOLS } from "@/lib/tools";
import type { ToolId } from "@/lib/types";

type Metric = "cost" | "tokens";

/** Bars never fill their band — the leftover is deliberate air. */
const MAX_BAR = 18;

interface UsageChartProps {
  initial: MonthlySeries;
  /**
   * Supplies another month's series. Defaults to the local API route; the
   * blob-fed dashboard passes a function that slices rows it already holds, so
   * changing month there costs no network at all.
   */
  loadMonth?: (month: string) => Promise<MonthlySeries | null>;
}

async function fetchMonth(month: string): Promise<MonthlySeries | null> {
  const res = await fetch(`/api/history?month=${month}`);
  return res.ok ? ((await res.json()) as MonthlySeries) : null;
}

export default function UsageChart({
  initial,
  loadMonth = fetchMonth,
}: UsageChartProps) {
  const [series, setSeries] = useState(initial);
  const [metric, setMetric] = useState<Metric>("cost");
  const [loading, setLoading] = useState(false);
  const [showTable, setShowTable] = useState(false);

  // Follow the parent when it swaps the data underneath us (a refresh, or a
  // re-decrypt after new settings) — otherwise the chart keeps rendering the
  // series it was first handed while every other panel has moved on. Adjusting
  // during render rather than in an effect: this is derived state, and an effect
  // would paint the stale series first and then immediately repaint.
  const [seenInitial, setSeenInitial] = useState(initial);
  // Whether the reader has deliberately chosen a month. Until they have, the
  // chart simply follows whatever the parent supplies — which is what makes a
  // tab left open across a month boundary roll over instead of sitting on the
  // old month. Once they have chosen, snapping them elsewhere mid-read would be
  // worse than letting their view age until they select again.
  const [picked, setPicked] = useState(false);
  if (initial !== seenInitial) {
    setSeenInitial(initial);
    if (!picked) setSeries(initial);
  }

  async function changeMonth(month: string) {
    setPicked(true);
    setLoading(true);
    try {
      const next = await loadMonth(month);
      if (next) setSeries(next);
    } catch {
      // Failure — keep the current series rather than crashing.
    } finally {
      setLoading(false);
    }
  }

  const value = (d: MonthlySeries["days"][number], id: ToolId) =>
    metric === "cost" ? d.perTool[id].costUsd : d.perTool[id].tokens;

  // Only chart tools with data this month. Colour follows the tool, so dropping
  // an idle one never repaints the others.
  const activeTools = useMemo(
    () => TOOLS.filter((t) => series.days.some((d) => value(d, t.id) > 0)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [series, metric],
  );

  const data = useMemo(
    () =>
      series.days.map((d) => {
        const row: Record<string, number | string> = { day: d.day.slice(8) };
        for (const t of TOOLS) row[t.id] = value(d, t.id);
        return row;
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [series, metric],
  );

  const monthTotal = useMemo(
    () =>
      series.days.reduce(
        (sum, d) => sum + TOOLS.reduce((s, t) => s + value(d, t.id), 0),
        0,
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [series, metric],
  );

  const fmtValue = (n: number) => (metric === "cost" ? usd(n) : compact(n));
  // Axis ticks round to clean numbers — cents on every gridline is noise.
  const fmtAxis = (n: number) =>
    metric === "cost"
      ? n >= 1000
        ? `$${compact(n)}`
        : `$${Math.round(n)}`
      : compact(n);

  return (
    <section className="rounded-xl border border-border bg-surface">
      <header className="flex flex-wrap items-center justify-between gap-3 border-b border-border px-4 py-3">
        <div>
          <h2 className="text-sm font-semibold">Daily usage</h2>
          <p className="mt-0.5 text-xs text-muted">
            <span className="tnum">{fmtValue(monthTotal)}</span> across{" "}
            <span className="tnum">{series.month}</span>
            {/* With one series there's no legend, so the subtitle names it. */}
            {activeTools.length === 1 && ` · ${activeTools[0].label}`}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Toggle value={metric} onChange={setMetric} />
          <select
            value={series.month}
            onChange={(e) => changeMonth(e.target.value)}
            aria-label="Month"
            className="rounded-lg border border-border bg-surface-2 px-2.5 py-1.5 text-xs text-foreground outline-none hover:border-border-strong focus-visible:border-border-strong"
          >
            {series.months.map((m) => (
              <option key={m} value={m}>
                {m}
              </option>
            ))}
          </select>
        </div>
      </header>

      {activeTools.length === 0 ? (
        <p className="px-4 py-12 text-center text-xs text-faint">
          No usage recorded in {series.month}.
        </p>
      ) : (
        <>
          {/* Hold the previous render at reduced opacity while refetching — no
              skeleton flash, no layout jump. */}
          <div
            className={`px-2 pt-4 transition-opacity ${loading ? "opacity-50" : ""}`}
          >
            <ResponsiveContainer width="100%" height={248}>
              <BarChart
                data={data}
                margin={{ top: 4, right: 12, bottom: 0, left: 4 }}
                barCategoryGap="18%"
              >
                <CartesianGrid stroke="var(--grid)" vertical={false} />
                <XAxis
                  dataKey="day"
                  tickLine={false}
                  axisLine={{ stroke: "var(--axis)" }}
                  tick={{ fill: "var(--faint)", fontSize: 11 }}
                  interval="preserveStartEnd"
                  minTickGap={14}
                />
                <YAxis
                  tickLine={false}
                  axisLine={false}
                  tick={{ fill: "var(--faint)", fontSize: 11 }}
                  tickFormatter={fmtAxis}
                  width={48}
                />
                <Tooltip
                  cursor={{ fill: "rgba(255,255,255,0.04)" }}
                  content={
                    <UsageTooltip month={series.month} fmtValue={fmtValue} />
                  }
                />
                {activeTools.map((t) => (
                  <Bar
                    key={t.id}
                    dataKey={t.id}
                    stackId="usage"
                    fill={t.color}
                    maxBarSize={MAX_BAR}
                    isAnimationActive={false}
                    shape={
                      <StackedBar seriesIds={activeTools.map((s) => s.id)} />
                    }
                  />
                ))}
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* A legend is always present for two or more series: identity is
              never carried by colour alone. A single series needs no legend —
              the heading already says what is plotted. */}
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2 px-4 pb-3 pt-1">
            {activeTools.length > 1 &&
              activeTools.map((t) => (
                <span
                  key={t.id}
                  className="flex items-center gap-1.5 text-[11px] text-muted"
                >
                  <span
                    aria-hidden
                    className="h-2 w-2 rounded-full"
                    style={{ background: t.color }}
                  />
                  {t.label}
                </span>
              ))}
            <button
              onClick={() => setShowTable((v) => !v)}
              className="ml-auto text-[11px] text-faint underline-offset-2 hover:text-muted hover:underline"
            >
              {showTable ? "Hide table" : "View as table"}
            </button>
          </div>

          {/* The WCAG-clean twin: every value reachable without colour or hover. */}
          {showTable && (
            <TableView
              series={series}
              tools={activeTools}
              value={value}
              fmtValue={fmtValue}
            />
          )}
        </>
      )}
    </section>
  );
}

/**
 * Stacked segment with a 2px surface gap separating it from the segment below,
 * and a 4px rounded cap on the topmost non-zero segment only (square at the
 * baseline). Recharts has no native stack gap, so the geometry is drawn here.
 */
function StackedBar(props: {
  seriesIds?: ToolId[];
  x?: number;
  y?: number;
  width?: number;
  height?: number;
  fill?: string;
  payload?: Record<string, number>;
  dataKey?: string;
}) {
  const { x = 0, y = 0, width = 0, height = 0, fill, payload, dataKey } = props;
  const seriesIds = props.seriesIds ?? [];
  if (height <= 0 || width <= 0) return null;

  const nonZero = seriesIds.filter((id) => (payload?.[id] ?? 0) > 0);
  // Topmost gets the rounded cap; bottom-most must stay welded to the baseline.
  const isTop = nonZero[nonZero.length - 1] === dataKey;
  const isBottom = nonZero[0] === dataKey;

  const GAP = 2;
  // The gap is carved out of the segment's lower edge, separating it from the
  // segment beneath — so it applies only when something is actually beneath.
  // The floor keeps a very short segment visible instead of collapsing it, and
  // stops a taller segment from rendering shorter than a smaller one.
  const h = isBottom ? height : Math.max(height - GAP, 1);
  const r = Math.min(4, width / 2, h);

  const d = isTop
    ? `M${x},${y + h} L${x},${y + r} Q${x},${y} ${x + r},${y} L${x + width - r},${y} Q${x + width},${y} ${x + width},${y + r} L${x + width},${y + h} Z`
    : `M${x},${y} h${width} v${h} h${-width} Z`;

  return <path d={d} fill={fill} />;
}

interface TooltipEntry {
  dataKey: ToolId;
  value: number;
  color: string;
}

function UsageTooltip({
  active,
  payload,
  label,
  month,
  fmtValue,
}: {
  active?: boolean;
  payload?: TooltipEntry[];
  label?: string;
  month: string;
  fmtValue: (n: number) => string;
}) {
  if (!active || !payload?.length) return null;

  const rows = payload.filter((p) => p.value > 0).reverse();
  if (rows.length === 0) return null;
  const total = rows.reduce((s, p) => s + p.value, 0);

  return (
    <div className="rounded-lg border border-border-strong bg-surface-2 px-3 py-2 text-xs shadow-xl">
      <p className="mb-1.5 font-medium text-foreground">
        {month}-{label}
      </p>
      <table className="w-full border-separate border-spacing-x-3 border-spacing-y-0.5">
        <tbody>
          {rows.map((p) => (
            <tr key={p.dataKey}>
              <td className="whitespace-nowrap text-muted">
                <span className="inline-flex items-center gap-1.5">
                  <span
                    aria-hidden
                    className="h-2 w-2 rounded-full"
                    style={{ background: p.color }}
                  />
                  {labelFor(p.dataKey)}
                </span>
              </td>
              <td className="tnum text-right font-mono text-foreground">
                {fmtValue(p.value)}
              </td>
            </tr>
          ))}
          {rows.length > 1 && (
            <tr>
              <td className="border-t border-border pt-1 text-faint">Total</td>
              <td className="tnum border-t border-border pt-1 text-right font-mono text-foreground">
                {fmtValue(total)}
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

function TableView({
  series,
  tools,
  value,
  fmtValue,
}: {
  series: MonthlySeries;
  tools: typeof TOOLS;
  value: (d: MonthlySeries["days"][number], id: ToolId) => number;
  fmtValue: (n: number) => string;
}) {
  const rows = series.days.filter((d) => tools.some((t) => value(d, t.id) > 0));
  return (
    <div className="max-h-72 overflow-auto border-t border-border">
      <table className="w-full text-xs">
        <thead className="sticky top-0 bg-surface-2 text-muted">
          <tr>
            <th className="px-3 py-2 text-left font-medium">Day</th>
            {tools.map((t) => (
              <th key={t.id} className="px-3 py-2 text-right font-medium">
                {t.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="tnum font-mono">
          {rows.map((d) => (
            <tr key={d.day} className="border-t border-border">
              <td className="px-3 py-1.5 text-muted">{d.day}</td>
              {tools.map((t) => (
                <td key={t.id} className="px-3 py-1.5 text-right">
                  {value(d, t.id) > 0 ? fmtValue(value(d, t.id)) : "—"}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Toggle({
  value,
  onChange,
}: {
  value: Metric;
  onChange: (m: Metric) => void;
}) {
  return (
    <div className="flex rounded-lg border border-border p-0.5 text-xs">
      {(["cost", "tokens"] as Metric[]).map((m) => (
        <button
          key={m}
          onClick={() => onChange(m)}
          aria-pressed={value === m}
          className={`rounded-md px-2.5 py-1 transition-colors ${
            value === m
              ? "bg-surface-3 text-foreground"
              : "text-muted hover:text-foreground"
          }`}
        >
          {m === "cost" ? "$ value" : "Tokens"}
        </button>
      ))}
    </div>
  );
}

function labelFor(id: ToolId): string {
  return TOOLS.find((t) => t.id === id)?.label ?? id;
}
